import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Mail, ArrowLeft, Package, Globe, Zap, CheckCircle2 } from 'lucide-react';
import AppLogoFull from '../components/AppLogoFull';
import LanguageSelector from '../components/LanguageSelector';
import { formatErrorMessage } from '../utils/errorHandler';
import { useLanguage } from '../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ForgotPassword = () => {
    const { t } = useLanguage();
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email) return;

        setLoading(true);
        setError('');
        setMessage('');

        try {
            const response = await fetch(`${BACKEND_URL}/api/auth/forgot-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email }),
            });
            const data = await response.json();
            if (response.ok) {
                setMessage(data.message);
                setEmail('');
            } else {
                setError(formatErrorMessage(data));
            }
        } catch (err) {
            setError(formatErrorMessage(err));
        }
        setLoading(false);
    };

    return (
        <div className="min-h-screen flex bg-[hsl(var(--background))]">
            <SEOHead title={t('forgotPasswordTitle')} description={t('forgotPasswordDesc')} noindex />

            {/* Left panel — hero image + branding overlay */}
            <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
                <img
                    src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=1200&q=80&auto=format"
                    alt="Logistics"
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />

                <button
                    onClick={() => navigate('/')}
                    className="absolute top-6 left-6 z-20 flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-lg border border-white/20 bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 transition-colors"
                >
                    <ArrowLeft size={16} />
                    {t('home')}
                </button>

                <div className="relative z-10 flex flex-col justify-end p-6 lg:p-10 w-full">
                    <h2 className="text-2xl lg:text-3xl font-bold text-white leading-tight">
                        {t('loginHeroTitle') || 'Simplifiez la gestion de votre fret'}
                    </h2>
                    <p className="mt-3 text-white/80 text-base max-w-md leading-relaxed">
                        {t('loginAIDesc')}
                    </p>

                    <div className="mt-6 flex flex-wrap gap-3">
                        <div className="flex items-center gap-2 rounded-full bg-white/15 backdrop-blur-sm px-4 py-2">
                            <Package size={16} className="text-white/90 shrink-0" />
                            <span className="text-sm text-white/90">{t('loginFeatureTracking')}</span>
                        </div>
                        <div className="flex items-center gap-2 rounded-full bg-white/15 backdrop-blur-sm px-4 py-2">
                            <Globe size={16} className="text-white/90 shrink-0" />
                            <span className="text-sm text-white/90">{t('loginFeatureMultiCountry')}</span>
                        </div>
                        <div className="flex items-center gap-2 rounded-full bg-white/15 backdrop-blur-sm px-4 py-2">
                            <Zap size={16} className="text-white/90 shrink-0" />
                            <span className="text-sm text-white/90">{t('loginFeatureNotifications')}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right panel — reset form */}
            <div className="flex-1 flex items-center justify-center p-4 relative">
                <div className="absolute top-4 right-4">
                    <LanguageSelector />
                </div>

                <Card className="w-full max-w-md border border-[hsl(var(--border))]">
                    <CardHeader className="space-y-1 flex flex-col items-center">
                        <AppLogoFull height={40} className="mb-4" />
                        <CardTitle className="text-xl font-bold text-center text-[hsl(var(--foreground))]">
                            {t('resetMyPassword')}
                        </CardTitle>
                        <CardDescription className="text-center">
                            {t('resetEmailDesc')}
                        </CardDescription>
                    </CardHeader>

                    <CardContent className="space-y-4">
                        {message ? (
                            <>
                                <Alert className="bg-green-50 border-green-200">
                                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                                    <AlertDescription className="text-green-800">{message}</AlertDescription>
                                </Alert>
                                <p className="text-sm text-muted-foreground text-center">
                                    <strong>{t('tip')} :</strong> {t('checkSpamTip')}
                                </p>
                            </>
                        ) : (
                            <>
                                {error && (
                                    <Alert variant="destructive">
                                        <AlertDescription>{error}</AlertDescription>
                                    </Alert>
                                )}

                                <form onSubmit={handleSubmit} className="space-y-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="email">{t('emailAddress')}</Label>
                                        <Input
                                            id="email"
                                            type="email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            placeholder={t('emailPlaceholderForgot')}
                                            required
                                            disabled={loading}
                                        />
                                    </div>

                                    <Button
                                        type="submit"
                                        className="w-full bg-accent hover:bg-accent/90"
                                        disabled={loading || !email}
                                    >
                                        {loading ? (
                                            <>
                                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                                                {t('sendingEmail')}
                                            </>
                                        ) : (
                                            <>
                                                <Mail className="h-4 w-4 mr-2" />
                                                {t('sendResetLink')}
                                            </>
                                        )}
                                    </Button>
                                </form>
                            </>
                        )}

                        <div className="pt-2">
                            <Link
                                to="/login"
                                className="flex items-center justify-center text-accent hover:text-accent/80 text-sm font-medium"
                            >
                                <ArrowLeft className="h-4 w-4 mr-1" />
                                {t('backToLogin')}
                            </Link>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default ForgotPassword;
